import {
  BookOpen,
  Trophy,
  Clock,
  Target,
  Play,
  CheckCircle,
  ChevronRight,
  Award,
  TrendingUp,
  Calendar,
  Zap,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import Layout from "../../layouts/Layout";
import StatusBadge from "../../components/common/StatusBadge";

export default function StudentDashboard() {
  const { user } = useAuth();

  const userName = user?.email?.split("@")[0] || "Student";
  const name = user?.name || userName;

  return (
    <Layout>
      <div className="w-full max-w-362.5 mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">

        {/* Progress hero */}
        <div
          className="qv-card qv-noise p-6 mb-5 sm:mb-6 flex flex-col md:flex-row md:items-center justify-between gap-6"
          style={{
            background:
              "linear-gradient(135deg, color-mix(in srgb, var(--color-primary) 10%, transparent), color-mix(in srgb, var(--color-accent-violet) 6%, transparent) 60%, transparent)",
          }}
        >
          <div>
            <div className="text-[11px] tracking-[0.22em] uppercase text-(--color-text-muted)">
              Your Progress
            </div>
            <h1 className="qv-serif mt-2 text-[32px] sm:text-[40px] leading-none font-medium tracking-tight text-(--color-text-primary)">
              Keep going, <span className="text-(--color-primary)">{name.split(" ")[0]}</span>.
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-2.5">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-(--color-primary-50) text-(--color-primary) border border-(--color-primary-100)">
                <Zap size={13} /> 6-day streak
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-600 border border-amber-500/20">
                <Trophy size={13} /> Rank #14 this month
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full text-(--color-text-secondary) border border-(--color-border)">
                <TrendingUp size={13} /> +18% vs last week
              </span>
            </div>
          </div>

          {/* Overall completion ring */}
          <div className="flex items-center gap-4 shrink-0">
            <div className="relative w-26 h-26">
              <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
                <circle cx="60" cy="60" r="52" fill="none" stroke="var(--color-border)" strokeWidth="10" />
                <circle
                  cx="60"
                  cy="60"
                  r="52"
                  fill="none"
                  stroke="var(--color-primary)"
                  strokeWidth="10"
                  strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 52}`}
                  strokeDashoffset={`${2 * Math.PI * 52 * (1 - 0.68)}`}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="qv-num text-[26px] leading-none text-(--color-text-primary)">68%</span>
                <span className="text-[9px] uppercase tracking-[0.18em] text-(--color-text-muted) mt-1">Complete</span>
              </div>
            </div>
            <div className="hidden sm:block text-xs text-(--color-text-muted) space-y-1.5">
              <div className="flex items-center gap-1.5"><CheckCircle size={13} className="text-(--color-primary)" /> 3 of 5 courses done</div>
              <div className="flex items-center gap-1.5"><Target size={13} className="text-(--color-text-secondary)" /> 2 due this week</div>
            </div>
          </div>
        </div>

        {/* Metric Cards — 2 cols on mobile, 4 on lg */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-5 sm:mb-6">
          {/* Courses Enrolled */}
          <div className="qv-card qv-noise p-4 sm:p-5">
            <div className="flex items-center gap-2 sm:gap-3 mb-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-(--color-primary)/20 flex items-center justify-center shrink-0">
                <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-(--color-primary)" />
              </div>
              <span className="text-xs sm:text-sm text-(--color-text-muted) dark:text-gray-400 leading-tight">
                Courses Enrolled
              </span>
            </div>
            <div className="flex items-baseline gap-1 mb-3">
              <span className="qv-num text-[30px] sm:text-[36px] leading-none text-(--color-primary)">
                5
              </span>
            </div>
            <button className="text-(--color-primary) text-xs sm:text-sm hover:underline flex items-center gap-1">
              Browse courses <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
            </button>
          </div>

          {/* Completed */}
          <div className="qv-card qv-noise p-4 sm:p-5">
            <div className="flex items-center gap-2 sm:gap-3 mb-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-green-500/20 flex items-center justify-center shrink-0">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
              </div>
              <span className="text-xs sm:text-sm text-(--color-text-muted) dark:text-gray-400 leading-tight">
                Completed
              </span>
            </div>
            <div className="qv-num text-[30px] sm:text-[36px] leading-none text-(--color-text-primary) mb-3">
              8
            </div>
            <span className="text-green-400 text-xs sm:text-sm">+3 this month</span>
          </div>

          {/* Learning Hours */}
          <div className="qv-card qv-noise p-4 sm:p-5">
            <div className="flex items-center gap-2 sm:gap-3 mb-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
              </div>
              <span className="text-xs sm:text-sm text-(--color-text-muted) dark:text-gray-400 leading-tight">
                Learning Hours
              </span>
            </div>
            <div className="qv-num text-[30px] sm:text-[36px] leading-none text-(--color-text-primary) mb-3">
              32.5h
            </div>
            <div className="flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-(--color-primary)" />
              <span className="text-(--color-primary) text-xs sm:text-sm">
                +8h this week
              </span>
            </div>
          </div>

          {/* Badges Earned */}
          <div className="qv-card qv-noise p-4 sm:p-5">
            <div className="flex items-center gap-2 sm:gap-3 mb-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-yellow-500/20 flex items-center justify-center shrink-0">
                <Trophy className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" />
              </div>
              <span className="text-xs sm:text-sm text-(--color-text-muted) dark:text-gray-400 leading-tight">
                Badges Earned
              </span>
            </div>
            <div className="qv-num text-[30px] sm:text-[36px] leading-none text-(--color-text-primary) mb-3">
              12
            </div>
            <button className="text-(--color-primary) text-xs sm:text-sm hover:underline flex items-center gap-1">
              View collection <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
            </button>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
          {/* Continue Learning */}
          <div className="lg:col-span-2 qv-card qv-noise p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4 sm:mb-5">
              <h2 className="text-base sm:text-lg font-semibold text-(--color-text-primary)">
                Continue Learning
              </h2>
              <StatusBadge variant="warning" label="In Progress" />
            </div>

            <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-5">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-(--color-primary)/20 flex items-center justify-center shrink-0">
                <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-(--color-text-primary) font-medium mb-1 text-sm sm:text-base truncate">
                  Introduction to Penetration Testing
                </h3>
                <p className="text-(--color-text-muted) dark:text-gray-400 text-xs sm:text-sm">
                  Lesson 7: Web Application Security Testing
                </p>
              </div>
            </div>

            <div className="mb-4 sm:mb-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs sm:text-sm text-(--color-text-muted) dark:text-gray-400">
                  Progress
                </span>
                <span className="text-xs sm:text-sm text-(--color-text-primary) font-medium">
                  72%
                </span>
              </div>
              <div className="h-1.5 sm:h-2 bg-(--color-bg-overlay) dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-linear-to-r from-(--color-primary) to-blue-500 rounded-full"
                  style={{ width: "72%" }}
                ></div>
              </div>
              <p className="text-xs text-(--color-text-muted) dark:text-gray-500 mt-2">
                3 lessons remaining • Estimated 2.5 hours
              </p>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <button className="flex-1 py-2 sm:py-2.5 bg-(--color-primary) text-(--color-text-on-primary) rounded-lg shadow-[0_0_10px_var(--color-shadow-primary)] hover:shadow-[0_0_16px_var(--color-shadow-primary-hover),0_0_32px_var(--color-shadow-primary)] transition-[color,background-color,box-shadow] duration-200 text-xs sm:text-sm font-medium flex items-center justify-center gap-2">
                <Play className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                Continue Course
              </button>
              <button className="px-3 sm:px-6 py-2 sm:py-2.5 bg-transparent border border-(--color-border) text-(--color-text-primary) rounded-lg hover:bg-white/5 dark:hover:bg-white/10 transition-colors text-xs sm:text-sm font-medium whitespace-nowrap">
                Course Details
              </button>
            </div>
          </div>

          {/* Daily Goals */}
          <div className="qv-card qv-noise p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4 sm:mb-5">
              <h2 className="text-base sm:text-lg font-semibold text-(--color-text-primary)">
                Daily Goals
              </h2>
              <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" />
            </div>

            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs sm:text-sm text-(--color-text-primary)">
                    Study Time
                  </span>
                  <span className="text-xs sm:text-sm font-medium text-(--color-primary)">
                    45/60 min
                  </span>
                </div>
                <div className="h-1.5 sm:h-2 bg-(--color-bg-overlay) dark:bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full bg-(--color-primary) rounded-full" style={{ width: "75%" }}></div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs sm:text-sm text-(--color-text-primary)">
                    Quizzes
                  </span>
                  <span className="text-xs sm:text-sm font-medium text-green-400">
                    2/2 completed
                  </span>
                </div>
                <div className="h-1.5 sm:h-2 bg-(--color-bg-overlay) dark:bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full bg-green-400 rounded-full" style={{ width: "100%" }}></div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs sm:text-sm text-(--color-text-primary)">
                    Practice Labs
                  </span>
                  <span className="text-xs sm:text-sm font-medium text-(--color-text-muted)">
                    0/1 completed
                  </span>
                </div>
                <div className="h-1.5 sm:h-2 bg-(--color-bg-overlay) dark:bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full bg-gray-500 rounded-full" style={{ width: "0%" }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
          {/* My Courses */}
          <div className="lg:col-span-2 qv-card qv-noise p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4 sm:mb-5">
              <h2 className="text-base sm:text-lg font-semibold text-(--color-text-primary)">
                My Courses
              </h2>
              <button className="text-(--color-primary) text-xs sm:text-sm hover:underline">
                View all
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {[
                { title: "Network Security Fundamentals", progress: 100, status: "Completed", color: "green" },
                { title: "Ethical Hacking Basics", progress: 45, status: "In Progress", color: "yellow" },
                { title: "Cryptography 101", progress: 20, status: "In Progress", color: "yellow" },
                { title: "Security+ Certification Prep", progress: 0, status: "Not Started", color: "gray" },
              ].map((course, index) => (
                <div
                  key={index}
                  className="p-3 sm:p-4 border border-(--color-border) rounded-lg hover:bg-(--color-bg-overlay) dark:hover:bg-white/5 transition-colors cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-3 gap-2">
                    <h3 className="text-xs sm:text-sm font-medium text-(--color-text-primary) flex-1 leading-snug">
                      {course.title}
                    </h3>
                    {course.color === "green" && (
                      <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-400 shrink-0" />
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-(--color-bg-overlay) dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          course.color === "green"
                            ? "bg-green-400"
                            : course.color === "yellow"
                            ? "bg-(--color-primary)"
                            : "bg-gray-500"
                        }`}
                        style={{ width: `${course.progress}%` }}
                      ></div>
                    </div>
                    <span className="text-xs text-(--color-text-muted) dark:text-gray-500 shrink-0">
                      {course.progress}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column — side by side on sm, stacked on lg */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4 sm:gap-6">
            {/* Upcoming */}
            <div className="qv-card qv-noise p-4 sm:p-6">
              <h2 className="text-base sm:text-lg font-semibold text-(--color-text-primary) mb-3 sm:mb-4">
                Upcoming
              </h2>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-red-500/20 flex items-center justify-center shrink-0">
                    <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-400" />
                  </div>
                  <div>
                    <h4 className="text-(--color-text-primary) text-xs sm:text-sm font-medium">
                      Final Exam
                    </h4>
                    <p className="text-(--color-text-muted) dark:text-gray-500 text-xs">
                      Network Security • Due Jan 28
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-yellow-500/20 flex items-center justify-center shrink-0">
                    <Target className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-yellow-400" />
                  </div>
                  <div>
                    <h4 className="text-(--color-text-primary) text-xs sm:text-sm font-medium">
                      Lab Assignment
                    </h4>
                    <p className="text-(--color-text-muted) dark:text-gray-500 text-xs">
                      Penetration Testing • Due Feb 01
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Achievements */}
            <div className="bg-linear-to-br from-(--color-primary)/10 to-blue-500/10 border border-(--color-primary)/30 rounded-xl p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-yellow-500/20 flex items-center justify-center shrink-0">
                  <Award className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-400" />
                </div>
                <div>
                  <h3 className="text-(--color-text-primary) font-medium text-sm sm:text-base">
                    New Badge!
                  </h3>
                  <p className="text-(--color-text-muted) dark:text-gray-400 text-xs sm:text-sm">
                    Network Defender
                  </p>
                </div>
              </div>
              <p className="text-xs text-(--color-text-muted) dark:text-gray-400">
                Earned for completing Network Security Fundamentals with 95% score
              </p>
            </div>
          </div>
        </div>

      </div>
    </Layout>
  );
}